
import
{
    Customer,
    CustomerDetails,
    CustomerPermissions,
    PaginatedResult,
    PaginationRequest
} from '@ews/react-data';

import
{
    useLoadingIndicator
} from "../Loading";

import
{
    useTopicData,
    useWebsocket
} from './ReactDataProvider';

import
{
    ReactTopics
} from '@ews/websocket-service';

import
{
    useCallback,
    useEffect,
    useState

} from 'react';

export const getCustomerDisplayName = (customer: Customer) =>
{
    const parts = [];
    if (customer.name) parts.push(`${customer.name}`);
    if (customer.customerNumber) parts.push(`[${customer.customerNumber}]`);

    return parts.join(" ");
};

export const useCustomerName = (customerId: string) =>
{
    const customer = useCustomer(customerId);
    return getCustomerDisplayName(customer);
};

export const useCustomer = (customerId: string) =>
{
    return useTopicData<Customer>(`${ReactTopics.customer.withParams({ customerId })}`, {} as Customer);
};

export const useCustomerDetails = (customerId: string) =>
{
    return useTopicData<CustomerDetails>(`${ReactTopics.customerDetails.withParams({ customerId })}`, {} as CustomerDetails);
};

export const useCustomerPermissions = (customerId: string) =>
{
    return useTopicData<CustomerPermissions>(`${ReactTopics.customerPermissions.withParams({ customerId })}`, {} as CustomerPermissions);
};
export const useMyCustomers = () =>
{
    const provider = useWebsocket();
    const [customers, setCustomers] = useState<Customer[]>([]);

    useEffect(() =>
    {
        const handleCustomer = (customerReceived: Customer) =>
        {
            setCustomers((customers) =>
            {
                return customers.map((customer) =>
                {
                    return customer.id === customerReceived.id ? customerReceived : customer;
                });
            });
        };

        const handleCustomers = ({ results }: { results: Customer[]; }) =>
        {
            const customersReceived = results;
            setCustomers((customers) =>
            {
                customers.forEach((customer) =>
                {
                    const topic = ReactTopics.customer.withParams({ customerId: customer.id! });
                    provider.unsubscribe(`${topic}`, handleCustomer);
                });

                customersReceived.forEach((customer) =>
                {
                    const topic = ReactTopics.customer.withParams({ customerId: customer.id! });
                    provider.subscribe(`${topic}`, handleCustomer);
                });

                return customersReceived;
            });
        };

        provider.bind(`${ReactTopics.myCustomers}`, handleCustomers);

        return () =>
        {
            provider.unsubscribe(`${ReactTopics.myCustomers}`, handleCustomers);
        };

    }, [provider]);

    return customers;
};



export const useModifyCustomer = () => 
{
    const loadingToggle = useLoadingIndicator();
    const provider = useWebsocket();

    return useCallback(async (customerId: string, data: Customer) => provider.modify(data, `${ReactTopics.customer.withParams({ customerId })}`, loadingToggle), [provider, loadingToggle]);
};

export const useModifyCustomerDetails = () =>
{
    const loadingToggle = useLoadingIndicator();
    const provider = useWebsocket();

    return useCallback(async (customerId: string, data: CustomerDetails) => provider.modify(data, `${ReactTopics.customerDetails.withParams({ customerId })}`, loadingToggle), [provider, loadingToggle]);
};

export const useModifyCustomerPermissions = () => 
{
    const loadingToggle = useLoadingIndicator();
    const provider = useWebsocket();

    return useCallback(async (customerId: string, data: CustomerPermissions) => provider.modify(data, `${ReactTopics.customerPermissions.withParams({ customerId })}`, loadingToggle), [provider, loadingToggle]);
};

export const useRemoveCustomer = () => 
{
    const loadingToggle = useLoadingIndicator();
    const provider = useWebsocket();

    return useCallback(async (customerId: string) => provider.delete(`${ReactTopics.customer.withParams({ customerId })}`, loadingToggle), [provider, loadingToggle]);
};

export const useCreateCustomer = () =>
{
    const loadingToggle = useLoadingIndicator();
    const provider = useWebsocket();

    return useCallback(async (name: string, parentId?: string) =>
    {
        const customer = { name, parentId } as Customer;
        return await provider.create(customer, `${ReactTopics.customerCreate}`, loadingToggle);
    }, [provider, loadingToggle]);
};

export const useFetchCustomerList = () =>
{
    const loadingToggle = useLoadingIndicator();
    const provider = useWebsocket();

    return useCallback(async (filterCriteria: PaginationRequest,) =>
    {
        return provider.fetch<PaginatedResult<Customer>>(filterCriteria, `${ReactTopics.myCustomers}`, loadingToggle);
    }, [loadingToggle, provider]);
};
export const useTypeAheadCustomerList = () =>
{
    const provider = useWebsocket();

    return useCallback(async (filterCriteria: PaginationRequest,) =>
    {
        return provider.fetch<PaginatedResult<Customer>>(filterCriteria, `${ReactTopics.myCustomers}`);
    }, [provider]);
};